/**
 * DogProfileHeader (#553) — the sticky identity band of the Pet Profile page.
 * The dog's name is the biggest text on the page (Principle 3 — the business
 * shows through; the pet's name is bigger than our labels). Photo (or an
 * initial-letter fallback — the COMMON case at HTHD, where Gingr photos were
 * never imported), a meta line, safety-first status chips (text label beside
 * color, never color-only), and exactly one primary action (Principle 2).
 *
 * Ted #28 Phase 2 (#1050) — the chips are the ONE registry's flags drawn by
 * the ONE renderer: the dog's (vax / INTACT / meds) beside the name, the
 * owner's (balance / credit / agreement) beside the owner. The header used
 * to draw its own vaccine-status and medication chips; those are gone, and
 * `flag-surface-coverage.test.ts` refuses their return. Kept as
 * header-owned: "Do not admit" (Phase 3, #1050) and the sex/altered word.
 *
 * #550 — staff photo capture: when there's no photo, staff tap the camera
 * affordance to take/attach one on the spot (a hidden file input with
 * `capture` opens the iPad/phone camera). An existing photo can be replaced
 * behind a confirm step. The upload persists to durable Blob storage
 * server-side; the card renders it immediately and it survives a reload.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { uploadDogPhoto, type DogProfile } from '../../lib/api.js';
// Ted #28 (#1050) — the profile shows the dog's, the owner's and the NEXT
// STAY's flags through the one renderer.
import { DogFlagChips } from '../dog-first/index.js';
import { resolveDogFlags } from '../dog-first/flag-skew.js';
import { OwnerLink } from '../owner-link/index.js';
import {
  deriveAge,
  humanizeAltered,
  humanizeSex,
} from './format.js';

interface DogProfileHeaderProps {
  dog: DogProfile;
  /** API base for the photo-upload call (#550). */
  apiUrl: string;
  /** Opens the Add-behavior-note form (the page's single primary action). */
  onAddNote: () => void;
  /** Sawyer 12 incident-wording-pet-notes (F3) — pets.notes:write:all; no button without it. */
  canAddNote?: boolean;
  /** #550 — a photo was uploaded; the parent updates its dog state so the
   *  new photo renders immediately (and survives a reload — the server
   *  persisted it). */
  onPhotoUpdated: (photoUrl: string) => void;
}

function initialOf(name: string): string {
  const t = name.trim();
  return t.length > 0 ? t[0]!.toUpperCase() : '?';
}

/** "Tue 9/23" for a YYYY-MM-DD stay day, in the browser's locale. */
function formatStayDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });
}

export function DogProfileHeader({
  dog,
  apiUrl,
  onAddNote,
  canAddNote = false,
  onPhotoUpdated,
}: DogProfileHeaderProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const showPhoto = Boolean(dog.photoUrl);

  const openPicker = (isReplace: boolean): void => {
    // #550 — replacing an existing photo is allowed WITH a confirm step.
    if (isReplace && !window.confirm(`Replace ${dog.name}’s photo with a new one?`)) {
      return;
    }
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    // Reset so picking the SAME file again still fires onChange.
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    const res = await uploadDogPhoto(apiUrl, dog.id, file);
    setUploading(false);
    if (res.data) {
      onPhotoUpdated(res.data.photoUrl);
      return;
    }
    setUploadError(res.error ?? 'Could not upload the photo. Please try again.');
  };

  const age = deriveAge(dog.birthDate);
  const meta = [
    dog.breed,
    dog.sizeCategory ? dog.sizeCategory : null,
    typeof dog.weight === 'number' ? `${dog.weight} lb` : null,
    age,
  ].filter((x): x is string => Boolean(x));

  const alteredWord = humanizeAltered(dog.altered);
  const sexWord = humanizeSex(dog.sex);
  // Ted #28 Phase 2 — the registry's list, with the deploy-window shim
  // filling vax / meds from what the profile already carries when the
  // server predates Phase 2 (`flag-skew.ts`).
  const flags = resolveDogFlags(dog, {
    hasMedications: dog.medications.some((m) => m.isActive),
  });

  return (
    <header className="ferro-dogprofile__header" data-testid="dogprofile-header">
      <div className="ferro-dogprofile__header-main">
        <div className="ferro-dogprofile__photo-wrap">
          {showPhoto ? (
            <img
              className="ferro-dogprofile__photo"
              src={dog.photoUrl as string}
              alt={dog.name}
            />
          ) : (
            <span
              className="ferro-dogprofile__photo ferro-dogprofile__photo--initial"
              aria-hidden="true"
            >
              {initialOf(dog.name)}
            </span>
          )}

          {/* #550 — staff photo capture. No photo → "Add photo"; existing
              photo → a "Replace" affordance behind a confirm step. The hidden
              input's `capture` opens the tablet/phone camera directly. */}
          <button
            type="button"
            className={
              showPhoto
                ? 'ferro-dogprofile__photo-replace'
                : 'ferro-dogprofile__photo-add'
            }
            onClick={() => openPicker(showPhoto)}
            disabled={uploading}
            data-testid="dogprofile-photo-add"
          >
            {uploading ? (
              'Uploading…'
            ) : (
              <>
                <span aria-hidden="true">📷</span>{' '}
                {showPhoto ? 'Replace photo' : 'Add photo'}
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => void onFileChange(e)}
            data-testid="dogprofile-photo-input"
          />
          {uploadError && (
            <p
              className="ferro-dogprofile__photo-error"
              role="alert"
              data-testid="dogprofile-photo-error"
            >
              {uploadError}
            </p>
          )}
        </div>

        <div className="ferro-dogprofile__ident">
          <h1 className="ferro-dogprofile__name">{dog.name}</h1>
          {meta.length > 0 && (
            <p className="ferro-dogprofile__meta">{meta.join(' · ')}</p>
          )}
          {/* Sawyer 01 owner-links (F2) — from the dog to the family in one tap. */}
          {dog.owner && (
            <p className="ferro-dogprofile__owner" data-testid="dogprofile-owner">
              <span className="ferro-dogprofile__owner-label">Owner</span>
              {' · '}
              <OwnerLink
                name={`${dog.owner.firstName} ${dog.owner.lastName}`.trim()}
                customerId={dog.owner.customerId}
                testId="dogprofile-owner-link"
              />
            </p>
          )}
          <div className="ferro-dogprofile__chips" data-testid="dogprofile-chips">
            {/* Ted #28 Phase 2 — the dog's flags (vax / INTACT / meds) from
                the one renderer. A current, altered, un-medicated dog shows
                nothing here: quiet IS the design (#546). */}
            <DogFlagChips flags={flags} scope="dog" testIdPrefix="dogprofile-" />
            {dog.isBanned && (
              <span
                className="ferro-dogprofile__chip ferro-dogprofile__chip--danger"
                data-testid="dogprofile-chip-banned"
              >
                Do not admit
              </span>
            )}
            {(alteredWord || sexWord) && (
              <span className="ferro-dogprofile__chip ferro-dogprofile__chip--neutral">
                {[sexWord, alteredWord].filter(Boolean).join(' · ')}
              </span>
            )}
            {/* Ted #28 (#1050) — "Transport in · Tue 9/23": the next (or
                current) stay that carries transport. Absent when none, and
                on a server older than #1050. */}
            {dog.nextTransportStay ? (
              <span
                className="ferro-dogprofile__chip-group"
                data-testid="dogprofile-chip-transport"
              >
                <DogFlagChips flags={dog.nextTransportStay.flags} testIdPrefix="dogprofile-" />
                <span className="ferro-dogprofile__chip-note">
                  {formatStayDay(dog.nextTransportStay.date)}
                </span>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="ferro-dogprofile__header-actions">
        {/* Sawyer 12 incident-wording-pet-notes (F3) — "Add pet note", writers only. */}
        {canAddNote ? (
          <button
            type="button"
            className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
            onClick={onAddNote}
            data-testid="dogprofile-add-note-trigger"
          >
            Add pet note
          </button>
        ) : null}
        {dog.owner && (
          <a
            className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
            href={`tel:${dog.owner.phone}`}
          >
            Call owner
          </a>
        )}
        {/* Ted #28 Phase 2 — the OWNER's flags beside the owner's action
            (money "follows underneath the client's name"). */}
        <span className="ferro-dogprofile__owner-flags" data-testid="dogprofile-owner-flags">
          <DogFlagChips flags={flags} scope="account" testIdPrefix="dogprofile-" />
        </span>
      </div>
    </header>
  );
}
